import React, { useEffect, useState, useCallback } from "react";
import {
    Card,
    CardContent,
    Grid,
    Box,
    Toolbar,
    IconButton,
    Tooltip,
    TextField,
    Typography,
    Button,
    Dialog,
    FormControl,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { ShippingParcelLineViewModel, ListQuery, DocumentNoProps } from "../../Types/ExportTypes";
import { useAuth, useSnackbar, useLoading } from "../../Context/Contexts";
import LocalizedNumberInputs from "../../components/Input/LocalizedNumberInputs"

import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { formatNumber } from '../../Utils/NumberUtils';
import { useTranslation } from "react-i18next";
import ResponsivePagination from "../ResponsivePagination";

const ShipmentParcelLines: React.FC<DocumentNoProps> = ({ documentNo }) => {
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const [query, setQuery] = useState<ListQuery>({ page: 0, pageSize: 2, filter: "" });
    const { showLoading, hideLoading } = useLoading();
    const [lines, setLines] = useState<ShippingParcelLineViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [editingLine, setEditingLine] = useState<ShippingParcelLineViewModel | null>(null);
    const { t } = useTranslation();


    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);



    const fetchLines = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await authService.post<{ items: ShippingParcelLineViewModel[]; count: number }>(
                `/shipmentDocumentApi/GetShipmentParcelLines?documentNo=${documentNo}`,
                query
            );
            setLines(response.items);
            setTotalCount(response.count);
        } catch (error) {
            console.error("Failed to fetch lines:", error);
        } finally {
            setIsLoading(false);
        }
    }, [authService, documentNo, query]);

    useEffect(() => {
        fetchLines();
        // fetchItemOptions("", 0).then(setItems);
    }, [fetchLines, query]);


    const handleEditRow = async (line: ShippingParcelLineViewModel) => {
        setIsLoading(true);
        setEditingLine(line);
        setIsLoading(false);
    };


    const handleFieldChange = async (field: keyof ShippingParcelLineViewModel, value: any) => {
        if (editingLine) {
            let updatedLine = { ...editingLine, [field]: value }
          


            setEditingLine((prev) => {
                if (!prev) return null;

                updatedLine = { ...prev, [field]: value };
                return updatedLine;
            });


        };
    }

    const handleSaveLine = async () => {

        if (!editingLine) return;


        try {
            setIsLoading(true);
            const updatedLine = await authService.post<ShippingParcelLineViewModel>("/shipmentDocumentApi/UpdateShipmentParcel", editingLine);

            if (editingLine.id !== 0) {
                // Update existing row in the list
                setLines((prev) =>
                    prev.map((line) => (line.id === updatedLine.id ? updatedLine : line))
                );
            } else {
                // Determine the correct page to navigate to
                const newTotalCount = totalCount + 1;
                const lastPage = Math.ceil(newTotalCount / query.pageSize) - 1;

                setQuery((prev) => ({
                    ...prev,
                    Page: lines.length < query.pageSize ? prev.page : lastPage,
                    totalCount: newTotalCount,
                }));
            }
            showSnackbar(`${t("dialog.lineSaved")}`, "success");
            setEditingLine(null);
        } catch (error: any) {
            console.error("Error saving line:", error);
            showSnackbar(`${t("dialog.lineSaveError")} :  ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const OpenWayBill = async (line: ShippingParcelLineViewModel, type: number) => {
        try {
            setIsLoading(true);
            // Call API with responseType 'blob' to handle PDF properly
            const response = await authService.post<Blob>(`/shipmentDocumentApi/OpenWaybill?type=${type}`, line, "blob");
            showSnackbar(t("dialog.downloadSuccess"), "success");
            // Convert response Blob to object URL
            const url = window.URL.createObjectURL(response);
            window.open(url, "_blank");

            // Optional: Cleanup memory
            setTimeout(() => window.URL.revokeObjectURL(url), 10000);
        } catch (error) {
            console.error("Error opening waybill:", error);
        }
        finally {
            setIsLoading(false);
        }
    };

    const PrintWayBill = async (line: ShippingParcelLineViewModel, type: number) => {
        try {
            setIsLoading(true);
            // Call API with responseType 'blob' to handle PDF properly
            await authService.post(`/shipmentDocumentApi/PrintWaybill?type=${type}`, line);

        } catch (error) {
            console.error("Error opening waybill:", error);
        }
        finally {
            setIsLoading(false);
        }
    };




    return (
        <Box sx={{ padding: "1rem" }}>


            <Box sx={{ padding: "1rem" }}>
                <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="center" alignItems="center">
                    {lines.map((line) => (
                        <Grid item xs={12} lg={6} key={line.id}>
                            <Card variant="outlined">

                                <Toolbar sx={{ backgroundColor: "primary.light", color: "white" }}>
                                    <Grid container spacing={1}>
                                        {/* Image */}
                                        <Grid item xs={4} >
                                            <Typography variant="h6" fontWeight="bold">
                                                {line.id}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4} >
                                            <Tooltip title={t("common.edit")}>
                                                <IconButton color="secondary" onClick={() => handleEditRow(line)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                        <Grid item xs={4} >

                                        </Grid>
                                    </Grid>
                                </Toolbar>



                                <CardContent>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12}>
                                            <Typography sx={{ color: "gray" }}>{line.parcelCode}</Typography>
                                        </Grid>
                                        {(() => {
                                            // Count how many items are displayed
                                            let count = 0;
                                            if (line.hasFormShipping) count++;
                                            if (line.hasFormReturn) count++;
                                            if (line.hasLabelShipping) count += 2;
                                            if (line.hasLabelReturn) count += 2;

                                            // Dynamically calculate xs value
                                            const xsValue = count > 0 ? Math.max(12 / count, 3) : 12;

                                            return (
                                                <>
                                                    {count > 0 && (
                                                        <>
                                                            <Box sx={{
                                                                backgroundColor: "secondary.main",
                                                                width: "100%"
                                                            }}>
                                                                <Grid container spacing={1}>
                                                                    {line.hasFormShipping && (
                                                                        <Grid item xs={xsValue}>

                                                                            <Tooltip title={t("common.openShippingForm")}>
                                                                                <IconButton onClick={() => OpenWayBill(line, 30)}>
                                                                                    <InsertDriveFileOutlinedIcon />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </Grid>
                                                                    )}
                                                                    {line.hasFormReturn && (
                                                                        <Grid item xs={xsValue}>
                                                                            <Tooltip title={t("common.openReturnForm")}>
                                                                                <IconButton onClick={() => OpenWayBill(line, 40)}>
                                                                                    <InsertDriveFileOutlinedIcon />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </Grid>
                                                                    )}
                                                                    {line.hasLabelShipping && (
                                                                        <Grid item xs={xsValue}>

                                                                            <Tooltip title={t("common.openShippingLabel")}>
                                                                                <IconButton onClick={() => OpenWayBill(line, 10)}>
                                                                                    <InsertDriveFileOutlinedIcon />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </Grid>
                                                                    )}
                                                                    {line.hasLabelReturn && (
                                                                        <Grid item xs={xsValue}>
                                                                            <Tooltip title={t("common.openReturnLabel")}>
                                                                                <IconButton onClick={() => OpenWayBill(line, 20)}>
                                                                                    <InsertDriveFileOutlinedIcon />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </Grid>
                                                                    )}
                                                                    {line.hasLabelShipping && (
                                                                        <Grid item xs={xsValue}>

                                                                            <Tooltip title={t("common.printShippingLabel")}>
                                                                                <IconButton onClick={() => PrintWayBill(line, 10)}>
                                                                                    <PrintOutlinedIcon />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </Grid>
                                                                    )}
                                                                    {line.hasLabelReturn && (
                                                                        <Grid item xs={xsValue}>

                                                                            <Tooltip title={t("common.printReturnLabel")}>
                                                                                <IconButton onClick={() => PrintWayBill(line, 20)}>
                                                                                    <PrintOutlinedIcon />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </Grid>
                                                                    )}
                                                                </Grid>
                                                            </Box>
                                                        </>
                                                    )}
                                                </>
                                            );
                                        })()}




               
                                        <Grid item xs={6}>
                                            <Typography variant="body2" > {t("common.netWeight")} (g):</Typography>
                                            <Typography variant="body1">
                                                {formatNumber(line.netWeight)}
                                            </Typography>

                                        </Grid>


                                        <Grid item xs={6}>
                                            <Typography variant="body2"> {t("common.grossWeight")} (g):</Typography>
                                            <Typography variant="body1">{formatNumber(line.grossWeight)}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2"> {t("common.volume")} (m³):</Typography>
                                            <Typography variant="body1">{formatNumber(line.volume)}</Typography>
                                        </Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="body2">{t("common.contents")}:</Typography>
                                            <Typography variant="body1">{line.contents ?? ''}</Typography>
                                        </Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="body2">{t("common.width")} (m):</Typography>
                                            <Typography variant="body1">{formatNumber(line.width)}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2">{t("common.height")} (m):</Typography>
                                            <Typography variant="body1">{formatNumber(line.height)}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2">{t("common.length")} (m):</Typography>
                                            <Typography variant="body1">{formatNumber(line.length)}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" >{t("common.packageType")}:</Typography>
                                            <Typography variant="body1" >{line.packageType}</Typography>
                                        </Grid>
                           
                                        <Grid item xs={12}>
                                            <Box sx={{ backgroundColor: "lightgreen", padding: "0.5rem", borderRadius: "4px", textAlign: "center" }}>
                                                <Typography variant="body1" >
                                                    {t("common.tracking")}: {line.waybillno}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>

                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
            <Dialog
                open={!!editingLine}
                onClose={() => setEditingLine(null)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: "8px", maxHeight: "80vh", overflowY: "auto" } }}
            >
                <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography variant="h6">{t("common.editLine")}</Typography>

                    {editingLine && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} lg={12}>
                                <Box sx={{ backgroundColor: "secondary.main" }}>
                                    <TextField
                                        label={t("common.parcelCode")}
                                        variant="outlined"
                                        fullWidth
                                        value={editingLine?.parcelCode || ""}
                                        onChange={(e) => handleFieldChange("parcelCode", e.target.value)}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} lg={12}>
                                <Box sx={{ backgroundColor: "secondary.main" }}>
                                    <TextField
                                        label={t("common.tracking")}
                                        variant="outlined"
                                        fullWidth
                                        value={editingLine?.waybillno || ""}
                                        onChange={(e) => handleFieldChange("waybillno", e.target.value)}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} lg={12}>
                                <Box sx={{ backgroundColor: "secondary.main" }}>
                                    <TextField
                                        label={t("common.contents")}
                                        variant="outlined"
                                        fullWidth
                                        value={editingLine?.contents || ""}
                                        onChange={(e) => handleFieldChange("contents", e.target.value)}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} lg={4}>
                                <Box>

                                    <LocalizedNumberInputs
                                        label={t("common.netWeight") + " (g)"}
                                        value={editingLine.netWeight ?? 0}
                                        onValueChange={(value) => {
                                            handleFieldChange("netWeight", value ?? null);
                                        }}
                                        allowNegative={false} // Prevents negative numbers
                                        decimalScale={4} // Ensures max 4 decimals
                                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} lg={4}>
                                <Box>

                                    <LocalizedNumberInputs
                                        label={t("common.grossWeight") + " (g)"}
                                        value={editingLine.grossWeight ?? 0}
                                        onValueChange={(value) => {
                                            handleFieldChange("grossWeight", value ?? null);
                                        }}
                                        allowNegative={false} // Prevents negative numbers
                                        decimalScale={4} // Ensures max 4 decimals
                                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} lg={4}>
                                <Box>

                                    <LocalizedNumberInputs
                                        label={t("common.volume") + " (m3)"}
                                        value={editingLine.volume ?? 0}
                                        onValueChange={(value) => {
                                            handleFieldChange("volume", value ?? null);
                                        }}
                                        allowNegative={false} // Prevents negative numbers
                                        decimalScale={4} // Ensures max 4 decimals
                                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                                    />
                                </Box>
                            </Grid>


                            <Grid item xs={12} lg={4}>
                                <Box>

                                    <LocalizedNumberInputs
                                        label={t("common.width") + " (m)"}
                                        value={editingLine.width ?? 0}
                                        onValueChange={(value) => {
                                            handleFieldChange("width", value ?? null);
                                        }}
                                        allowNegative={false} // Prevents negative numbers
                                        decimalScale={4} // Ensures max 4 decimals
                                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12} lg={4}>
                                <Box>

                                    <LocalizedNumberInputs
                                        label={t("common.height") + " (m)"}
                                        value={editingLine.height ?? 0}
                                        onValueChange={(value) => {
                                            handleFieldChange("height", value ?? null);
                                        }}
                                        allowNegative={false} // Prevents negative numbers
                                        decimalScale={4} // Ensures max 4 decimals
                                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} lg={4}>
                                <Box>

                                    <LocalizedNumberInputs
                                        label={t("common.length") + " (m)"}
                                        value={editingLine.length ?? 0}
                                        onValueChange={(value) => {
                                            handleFieldChange("length", value ?? null);
                                        }}
                                        allowNegative={false} // Prevents negative numbers
                                        decimalScale={4} // Ensures max 4 decimals
                                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} lg={12}>
                                <Box sx={{ backgroundColor: "secondary.main" }}>
                                    <TextField
                                        label={t("common.packageType")}
                                        variant="outlined"
                                        fullWidth
                                        value={editingLine?.packageType || ""}
                                        onChange={(e) => handleFieldChange("packageType", e.target.value)}
                                    />
                                </Box>
                            </Grid>

                        </Grid>
                    )}

                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                        <Button onClick={handleSaveLine} variant="contained" startIcon={<SaveIcon />} disabled={!editingLine}>
                            {t("common.save")}
                        </Button>
                        <Button onClick={() => setEditingLine(null)} variant="outlined" startIcon={<CancelIcon />}>
                            {t("common.cancel")}
                        </Button>

                    </Box>
                </Box>
            </Dialog>

            <ResponsivePagination
                page={query.page}
                pageSize={query.pageSize}
                totalCount={totalCount}
                onPageChange={(newPage) => setQuery({ ...query, page: newPage })}
                sx={{ marginTop: "1rem" }}
            />

        </Box>
    );
};

export default ShipmentParcelLines;